import cx from 'classnames';
import { ConfigProvider, DatePicker } from 'antd';
import { useEffect, useRef, useState } from 'react';

import { getLocale } from '../../../utils/locale';
import { getLanguage } from '../../../utils/language';
import { GenericParam } from '../../interfaces/commons';
import { DATE_FORMATS, LANGUAGES } from '../../../config/commons';
import { LrvDatePickerProps } from '../../interfaces/lrvDatePicker';

import styles from './LrvDatePickerV2.module.scss';

export const LrvDatePickerV2 = (props: LrvDatePickerProps) => {
  const { label, titleClassName, containerClassName, className, theme = 'dark', value, disabled, hasError, placeholder = '' } = props;
  const isDarkTheme = theme === 'dark';

  const inputRef = useRef<InstanceType<typeof DatePicker>>(null);
  const [isFocused, setIsFocused] = useState(false);
  const hasValue = !!value;
  
  useEffect(() => {
    if (!hasValue) {
      setIsFocused(false);
    }
  }, [hasValue]);

  const handleClick = () => {
    if (disabled) {
      return;
    }

    (inputRef.current as GenericParam)?.focus();
    setIsFocused(true);
  };

  const handleSelect = () => {
    setTimeout(() => {
      setIsFocused(false);
    }, 0);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const lrvDatePickerProps = {
    ...props,
    className: cx(isDarkTheme ? styles.darkDatePicker : styles.lightDatePicker, className),
    popupClassName: cx(isDarkTheme ? styles.darkDropdown : styles.lightDropdown),
    placeholder,
    onSelect: handleSelect,
    ref: inputRef,
    onBlur: handleBlur,
    onFocus: handleFocus,
  };

  const getFormat = () => {
    const language = getLanguage().toUpperCase();

    if (lrvDatePickerProps.format) {
      return lrvDatePickerProps.format;
    }

    switch (language) {
      case LANGUAGES.ENGLISH:
      default:
        return DATE_FORMATS.MMM_DD_YYYY;

      case LANGUAGES.PORTUGUESE:
      case LANGUAGES.SPANISH:
        return DATE_FORMATS.DD_MMM_YYYY;
    }
  };

  return (
    <div
      className={cx(
        styles.container,
        isDarkTheme ? styles.darkContainer : styles.lightContainer,
        containerClassName,
        disabled ? styles.disabled : '',
        isFocused ? styles.focus : '',
        hasError ? styles.errorBorder : '',
      )}
      onClick={handleClick}
    >
      <div
        className={cx(
          styles.title,
          isDarkTheme ? styles.titleDark : styles.titleLight,
          titleClassName,
          (hasValue || isFocused) ? '' : styles.titleUnfocused
        )}
      >
        {label}
      </div>
      
      <ConfigProvider locale={getLocale()}>
        <DatePicker {...lrvDatePickerProps} format={getFormat()}/>
      </ConfigProvider>
    </div>
  );
};
